import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSolution, getSolutionByPath } from '../../api/content';
import UnifiedInquirySection from '../../components/features/UnifiedInquirySection';
import FilePreviewModal from '../../components/common/FilePreviewModal';
import ShareButton from '../../components/common/ShareButton';
import './Solutions.css';
import './SolutionDetail.css';

const SolutionDetail = () => {
    const { category, slug } = useParams();
    const navigate = useNavigate();
    const [solution, setSolution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalImage, setModalImage] = useState(null); // 이미지 팝업용
    const [manualModalOpen, setManualModalOpen] = useState(false); // 메뉴얼 미리보기용
    const [proposalModalOpen, setProposalModalOpen] = useState(false); // 제안서 미리보기용
    const contentRef = useRef(null);

    useEffect(() => {
        loadSolution();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, slug]);

    // Keep the browser tab title in sync with the loaded solution. The Layout
    // skips detail-page paths so it won't overwrite this; on unmount we restore
    // the brand title so back-navigation doesn't strand the previous title.
    useEffect(() => {
        if (solution?.title) {
            document.title = `${solution.title} | 가까운솔루션`;
        }
        return () => {
            document.title = '가까운솔루션';
        };
    }, [solution]);

    const loadSolution = async () => {
        try {
            // 정본 경로로 조회한다. 앱 내부에서 구 주소(숫자 id)로 넘어온 경우에만
            // id 조회로 떨어지고, 받아오면 곧장 정본 경로로 갈아탄다 — 주소창에
            // 구 주소가 남아 있으면 그 상태로 공유·북마크된다.
            const data = /^\d+$/.test(category) && !slug
                ? await getSolution(category)
                : await getSolutionByPath(category, slug);
            setSolution(data);
            if (data?.path && window.location.pathname !== data.path) {
                navigate(data.path, { replace: true });
            }
        } catch (err) {
            console.error('Failed to load solution', err);
            setError('솔루션을 찾을 수 없습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 이벤트 위임을 사용한 이미지 클릭 핸들러
    const handleContentClick = (e) => {
        if (e.target.tagName === 'IMG') {
            e.preventDefault();
            setModalImage(e.target.src);
        }
    };

    const handleDownloadManual = async () => {
        if (!solution?.manualFile) return;
        try {
            const fileUrl = solution.manualFile;
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${solution.title}_설명서.${solution.manualFile.split('.').pop()}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            alert('파일 다운로드에 실패했습니다.');
        }
    };

    const handleDownloadProposal = async () => {
        if (!solution?.proposalFile) return;
        try {
            const fileUrl = solution.proposalFile;
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${solution.title}_제안서.${solution.proposalFile.split('.').pop()}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            alert('파일 다운로드에 실패했습니다.');
        }
    };

    const renderDocumentButtons = () => (
        <div className="doc-btn-group">
            {solution.manualFile && (
                <div className="doc-btn">
                    <div className="doc-info">
                        <span>📘</span> 제품 설명서
                    </div>
                    <div className="doc-actions">
                        <button
                            className="doc-action-item"
                            onClick={(e) => { e.stopPropagation(); setManualModalOpen(true); }}
                            title="미리보기"
                        >
                            🔍
                        </button>
                        <div className="doc-divider"></div>
                        <button
                            className="doc-action-item"
                            onClick={(e) => { e.stopPropagation(); handleDownloadManual(); }}
                            title="다운로드"
                        >
                            💾
                        </button>
                    </div>
                </div>
            )}

            {solution.proposalFile && (
                <div className="doc-btn">
                    <div className="doc-info">
                        <span>📋</span> 제안서
                    </div>
                    <div className="doc-actions">
                        <button
                            className="doc-action-item"
                            onClick={(e) => { e.stopPropagation(); setProposalModalOpen(true); }}
                            title="미리보기"
                        >
                            🔍
                        </button>
                        <div className="doc-divider"></div>
                        <button
                            className="doc-action-item"
                            onClick={(e) => { e.stopPropagation(); handleDownloadProposal(); }}
                            title="다운로드"
                        >
                            💾
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="solutions-page">
                <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    if (error || !solution) {
        return (
            <div className="solutions-page">
                <div className="container" style={{ padding: '4rem 0', textAlign: 'center' }}>
                    <h2>{error || '솔루션을 찾을 수 없습니다.'}</h2>
                    <button
                        onClick={() => navigate('/solutions')}
                        className="btn btn-primary"
                        style={{ marginTop: '1rem' }}
                    >
                        목록으로 돌아가기
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="solutions-page">
            {/* Hero Header with Ambient Blur (Option A Layout) */}
            <div className="page-hero solution-detail-hero" style={{ position: 'relative', overflow: 'hidden' }}>
                {/* 1. Underlying Blurred Ambient Background */}
                <div 
                    className="solution-hero-bg"
                    style={{
                        backgroundImage: solution.thumbnail ? `url(${solution.thumbnail})` : 'none',
                        backgroundColor: solution.thumbnail ? 'transparent' : '#1a1a1a'
                    }}
                />
                
                {/* 2. Text Contrast Overlay */}
                <div className="solution-hero-overlay"></div>

                {/* 3. Foreground Content */}
                <div className="container" style={{ position: 'relative', zIndex: 10 }}>
                    {/*
                      상세에서는 업종을 전부 보여 준다. 목록 카드와 다르게 공간이 있고,
                      "이거 우리 업종에도 쓰이는구나"가 방문자에게 실제로 쓸모 있는 정보다.
                      대표는 채운 배지, 추가는 테두리 배지로 위계를 남긴다.
                    */}
                    {(solution.categories?.length > 0 || solution.categoryLabel) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '1rem' }}>
                            {(solution.categories?.length
                                ? solution.categories
                                : [{ id: 'legacy', label: solution.categoryLabel, isPrimary: true }]
                            ).map((c) => (
                                <span
                                    key={c.id}
                                    style={{
                                        // 추가 배지는 투명 배경이면 히어로 이미지 위에서 안 읽힌다.
                                        // 흰 면을 깔고 글자는 대비가 확보된 짙은 핑크(5.94:1)를 쓴다.
                                        background: c.isPrimary ? 'var(--primary-color)' : 'rgba(255,255,255,0.94)',
                                        color: c.isPrimary ? 'white' : 'var(--primary-text)',
                                        border: '1px solid var(--primary-color)',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '0.85rem',
                                        display: 'inline-block',
                                    }}
                                >
                                    {c.label}
                                </span>
                            ))}
                        </div>
                    )}
                    <h1 className="page-title">{solution.title}</h1>
                    {solution.subtitle && <p className="page-subtitle">{solution.subtitle}</p>}
                </div>
            </div>

            {/* Content Body */}
            <section style={{ padding: '4rem 0', backgroundColor: 'white' }}>
                <div className="container" style={{ maxWidth: '900px' }}>

                    {/* Content Top Spacer removed for immersive UX */}

                    <div
                        ref={contentRef}
                        className="solution-content"
                        onClick={handleContentClick}
                        dangerouslySetInnerHTML={{ __html: solution.content }}
                    />
                    
                    {/* Social Share Module */}
                    <ShareButton 
                        url={`${window.location.origin}${solution.path || `/solutions/${solution.id}`}`}
                        title={`[가까운솔루션] ${solution.title}`}
                        description={solution.subtitle}
                        imageUrl={solution.thumbnail ? `https://www.ggusol.com${solution.thumbnail}` : null}
                    />
                </div>
            </section>

            {/* Floating Action Bar (Glassmorphism Pill) */}
            <div className="floating-action-bar">
                <button
                    onClick={() => navigate('/solutions')}
                    className="fab-back-btn"
                >
                    <span style={{ fontSize: '1.2rem', marginRight: '6px' }}>←</span> 목록으로 돌아가기
                </button>

                {(solution.manualFile || solution.proposalFile) && (
                    <>
                        <div className="fab-divider"></div>
                        {renderDocumentButtons()}
                    </>
                )}
            </div>

            <UnifiedInquirySection title="이 솔루션이 우리 현장에 맞는지 무료로 체험해보세요." buttonText="💻 해당 솔루션 데모 신청" buttonAction="DEMO" />

            {/* Image Modal */}
            {modalImage && (
                <div className="image-modal-overlay" onClick={() => setModalImage(null)}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="image-modal-close" onClick={() => setModalImage(null)}>✕</button>
                        <img src={modalImage} alt="확대 이미지" />
                    </div>
                </div>
            )}

            {/* Manual Preview Modal */}
            <FilePreviewModal
                isOpen={manualModalOpen}
                onClose={() => setManualModalOpen(false)}
                fileUrl={solution.manualFile}
                fileName={`${solution.title} 설명서`}
            />

            {/* Proposal Preview Modal */}
            <FilePreviewModal
                isOpen={proposalModalOpen}
                onClose={() => setProposalModalOpen(false)}
                fileUrl={solution.proposalFile}
                fileName={`${solution.title} 제안서`}
            />
        </div>
    );
};

export default SolutionDetail;